import React, { useState, useEffect } from 'react';
import { FiPlus, FiCheck } from 'react-icons/fi';
import client from '../api/client';

export default function PostOpAppointmentList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ appointmentDate: '', providerName: '', appointmentType: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchAppointments = async () => {
    try {
      const res = await client.get('/recovery/appointments');
      setAppointments(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAppointments(); }, []);

  const handleAdd = async () => {
    if (!form.appointmentDate) return;
    setSaving(true);
    try {
      await client.post('/recovery/appointments', form);
      setForm({ appointmentDate: '', providerName: '', appointmentType: '', notes: '' });
      setShowForm(false);
      fetchAppointments();
    } catch {
      alert('Failed to add appointment.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await client.put(`/recovery/appointments/${id}`, { isCompleted: true });
      fetchAppointments();
    } catch {
      alert('Failed to update appointment.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`/recovery/appointments/${id}`);
      fetchAppointments();
    } catch {
      alert('Failed to delete appointment.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
      </div>
    );
  }

  const upcoming = appointments.filter(a => !a.isCompleted);
  const past = appointments.filter(a => a.isCompleted);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-white/60">Upcoming Appointments</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition"
        >
          <FiPlus size={14} /> Add
        </button>
      </div>

      {showForm && (
        <div className="bg-white/5 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={form.appointmentDate}
              onChange={e => setForm({ ...form, appointmentDate: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Provider name"
              value={form.providerName}
              onChange={e => setForm({ ...form, providerName: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
            />
          </div>
          <input
            type="text"
            placeholder="Appointment type (e.g. Post-op follow-up)"
            value={form.appointmentType}
            onChange={e => setForm({ ...form, appointmentType: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Notes..."
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || !form.appointmentDate}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition"
            >
              {saving ? 'Adding...' : 'Add Appointment'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-white/10 text-white/60 rounded-lg text-sm hover:bg-white/20 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {upcoming.length === 0 && !showForm && (
        <p className="text-white/30 text-sm text-center py-4">No upcoming appointments</p>
      )}

      {upcoming.map(appt => (
        <div key={appt.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
          <div className="flex-1">
            <p className="text-white font-medium text-sm">{appt.appointmentDate}</p>
            <p className="text-xs text-white/40 mt-0.5">
              {appt.providerName && `${appt.providerName} · `}{appt.appointmentType}
            </p>
            {appt.notes && <p className="text-xs text-white/30 mt-1">{appt.notes}</p>}
          </div>
          <button
            onClick={() => handleComplete(appt.id)}
            className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 transition"
          >
            <FiCheck size={12} /> Done
          </button>
          <button
            onClick={() => handleDelete(appt.id)}
            className="text-xs text-white/30 hover:text-red-400 transition"
          >
            Delete
          </button>
        </div>
      ))}

      {past.length > 0 && (
        <>
          <h3 className="text-sm font-medium text-white/40 mt-6">Completed</h3>
          {past.map(appt => (
            <div key={appt.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl opacity-60">
              <div className="flex-1">
                <p className="text-white/70 font-medium text-sm">{appt.appointmentDate}</p>
                <p className="text-xs text-white/30 mt-0.5">
                  {appt.providerName && `${appt.providerName} · `}{appt.appointmentType}
                </p>
                {appt.followUpInstructions && (
                  <p className="text-xs text-white/30 mt-1">Follow-up: {appt.followUpInstructions}</p>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
